import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAsync — load something, and be honest about the three states it can be in.
 *
 * Deliberately not a data-fetching library. The workbench has a small number of
 * loads, each of which must show its own error (including the `requestId` that
 * finds the server log), and a cache that silently served a stale diagram would
 * be a correctness problem, not a performance win.
 */
export interface AsyncState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  /** Re-run the loader. */
  reload: () => void;
  /** Replace the value locally — for a response the caller already holds. */
  set: (value: T) => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[], options: { enabled?: boolean } = {}): AsyncState<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(enabled);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    const current = ++generation.current;
    setLoading(true);
    setError(undefined);
    load().then(
      value => { if (current === generation.current) { setData(value); setLoading(false); } },
      cause => { if (current === generation.current) { setError(cause); setLoading(false); } },
    );
    // The loader is captured per-render on purpose; `deps` is the caller's
    // statement of what the load actually depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled]);

  const reload = useCallback(() => setNonce(n => n + 1), []);
  const set = useCallback((value: T) => setData(value), []);

  return { data, error, loading, reload, set };
}
